import React, { useState } from "react";
import axios from "axios";
import { Download, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { useTurnstile, TurnstileFallback } from "../hooks/useTurnstile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical executive CV — single source of truth, hot-swappable.
export const CV_FILE = "/brand-kit/JJ_Argent_Executive_CV.pdf";
export const CV_FILENAME = "JJ Argent Executive Resume.pdf";

const ROLE_TYPES = [
  { value: "events", label: "Events & festivals" },
  { value: "hospitality", label: "Hospitality & venues" },
  { value: "corporate", label: "Corporate operations" },
  { value: "university", label: "Universities & higher ed" },
  { value: "community", label: "Community & not-for-profit" },
  { value: "government", label: "Government & public sector" },
  { value: "other", label: "Something else" },
];

/**
 * Hybrid CV delivery — zero-friction instant download for the canonical
 * Executive CV, plus a secondary tailored-CV request form.
 * Used on the Recruiter page (compact=false) and the Home page ("Get the CV" FAB, compact=true).
 */
export default function CVBlock({ compact = false }) {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", organisation: "", role_type: "events", context: "" });
  const { containerRef, token, reset, scriptFailed } = useTurnstile();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    if (!token) {
      setError("Please complete the verification step above.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        type: "recruitment",
        full_name: form.name.trim(),
        email: form.email.trim(),
        organisation: form.organisation.trim(),
        message: `Tailored CV request — role type: ${form.role_type}${form.context ? `\nContext: ${form.context}` : ""}`,
        turnstile_token: token,
      });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong — please try again.";
      setError(typeof msg === "string" ? msg : "Something went wrong — please try again.");
      reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full" data-testid="cv-block">
      {/* Primary: instant download, no gate */}
      <a
        href={CV_FILE}
        download={CV_FILENAME}
        data-testid="cv-download-primary"
        className="btn-primary w-full justify-center !py-4 !text-[13px]"
      >
        <Download size={16} /> Download Executive CV (PDF)
      </a>
      <p className="mt-3 text-[11px] tracking-[0.14em] uppercase text-[#A9A9A6] text-center">
        Instant · No form · One canonical file
      </p>

      {/* Secondary: tailored variant request */}
      {!compact && (
        <div className="mt-10 border-t border-[#343437] pt-8" data-testid="cv-tailored-block">
          <p className="eyebrow">Need it tailored?</p>
          <h3 className="mt-3 font-display text-xl md:text-2xl leading-tight text-[#FAF9F6]">
            Want this rewritten for events, hospitality or corporate operations?
          </h3>
          <p className="mt-3 text-sm text-[#A9A9A6] leading-relaxed max-w-xl">
            Tell me the role type and any context; I'll send a version rewritten for it within two business days.
          </p>

          {!showForm && !done && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              data-testid="cv-tailored-open"
              className="btn-ghost mt-5"
            >
              Request a tailored CV <ArrowUpRight size={14} />
            </button>
          )}

          {done && (
            <div
              role="status"
              data-testid="cv-tailored-success"
              className="mt-6 border border-[#D8A936]/50 bg-[#D8A936]/5 p-5 flex items-start gap-3"
            >
              <CheckCircle2 size={18} className="text-[#D8A936] shrink-0 mt-0.5" />
              <div>
                <p className="font-display text-sm text-[#FAF9F6]">Request received.</p>
                <p className="mt-1 text-xs text-[#A9A9A6] leading-relaxed">
                  I'll send a tailored version within two business days.
                </p>
              </div>
            </div>
          )}

          {showForm && !done && (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="cv-tailored-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block text-xs tracking-[0.14em] uppercase text-[#A9A9A6]">
                  Name*
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="cv-form-name"
                    className="mt-2 w-full bg-[#0B0B0C] border border-[#343437] px-4 py-3 text-[15px] text-[#FAF9F6] focus:border-[#D8A936] focus:outline-none normal-case tracking-normal"
                  />
                </label>
                <label className="block text-xs tracking-[0.14em] uppercase text-[#A9A9A6]">
                  Email*
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="cv-form-email"
                    className="mt-2 w-full bg-[#0B0B0C] border border-[#343437] px-4 py-3 text-[15px] text-[#FAF9F6] focus:border-[#D8A936] focus:outline-none normal-case tracking-normal"
                  />
                </label>
                <label className="block text-xs tracking-[0.14em] uppercase text-[#A9A9A6]">
                  Organisation
                  <input
                    type="text"
                    value={form.organisation}
                    onChange={(e) => setForm({ ...form, organisation: e.target.value })}
                    data-testid="cv-form-organisation"
                    className="mt-2 w-full bg-[#0B0B0C] border border-[#343437] px-4 py-3 text-[15px] text-[#FAF9F6] focus:border-[#D8A936] focus:outline-none normal-case tracking-normal"
                  />
                </label>
                <label className="block text-xs tracking-[0.14em] uppercase text-[#A9A9A6]">
                  Role type
                  <select
                    value={form.role_type}
                    onChange={(e) => setForm({ ...form, role_type: e.target.value })}
                    data-testid="cv-form-role-type"
                    className="mt-2 w-full bg-[#0B0B0C] border border-[#343437] px-4 py-3 text-[15px] text-[#FAF9F6] focus:border-[#D8A936] focus:outline-none normal-case tracking-normal"
                  >
                    {ROLE_TYPES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="block text-xs tracking-[0.14em] uppercase text-[#A9A9A6]">
                Anything specific to lean into? (optional)
                <textarea
                  rows={3}
                  value={form.context}
                  onChange={(e) => setForm({ ...form, context: e.target.value })}
                  data-testid="cv-form-context"
                  className="mt-2 w-full bg-[#0B0B0C] border border-[#343437] px-4 py-3 text-[15px] text-[#FAF9F6] focus:border-[#D8A936] focus:outline-none normal-case tracking-normal"
                />
              </label>
              <div ref={containerRef} data-testid="cv-turnstile" />
              <TurnstileFallback show={scriptFailed} />
              {error && (
                <p role="alert" data-testid="cv-form-error" className="text-xs text-[#EF4444]">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                data-testid="cv-form-submit"
                className="btn-primary w-full justify-center disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Send request"} <ArrowUpRight size={14} />
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
